/* eslint-disable sonarjs/cognitive-complexity */
import React from 'react';

import {
  BaSeBreakpointContext,
  BaSeDialogContext,
  BaSeHeading6,
  BaSeIcon,
  BaSeParagraph,
  BaSeShapeButton,
  BaSeTabs,
  BaSeTag,
  BaSeTextArea,
  BaSeTheme,
} from '@base/react';

import SpeechRecognition, {
  useSpeechRecognition,
} from 'react-speech-recognition';
import { useAnswerController } from '../../../../hooks/controllers/answer-controller';
import { useInterviewContext } from '../../context/interview-context';
import Lista from '../../lista';
import CommonButton from '../common-button/common-button';
import {
  Container,
  ConteinerButtons,
  ConteinerTextArea,
  Content,
  ContentMic,
  ContentPergunta,
  ContentSaving,
  ContentTextarea,
  InfoBody,
  InfoHeader,
  SubTitle,
  TabsWrapper,
  Title,
  Wrapper,
} from './questionnaire-interview-style';

const questionMap = {
  1: '1 - Descreva uma situação em que você fez alguma coisa por si mesmo.',
  2: '2 - Descreva uma situação em que você teve que conseguir que alguém fizesse algo que você queria.',
  3: '3 - Uma situação em que você teve muita dificuldade de conseguir algo.',
  4: '4 - Descreva uma situação em que você ficou satisfeito por ter realizado algo.',
  5: '5 - Conte-me uma situação em que você correu um risco.',
};

const QuestionnaireInterviewCommon = ({
  onQuestionChange,
  setFinishedInterview,
  setStatusInterview,
}) => {
  const { interview, respostas, setRespostas } = useInterviewContext();
  const { prepareAnswers, updateAnswer, sendAnswerInterview } =
    useAnswerController();
  const { showModal } = React.useContext(BaSeDialogContext);
  const breakpointController = React.useContext(BaSeBreakpointContext);
  const {
    transcript,
    listening,
    resetTranscript,
    browserSupportsSpeechRecognition,
  } = useSpeechRecognition();

  const [activeTabIndex, setActiveTabIndex] = React.useState(0);
  const [currentAnswer, setCurrentAnswer] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [hasSaved, setHasSaved] = React.useState(false);
  const [showValidation, setShowValidation] = React.useState(false);
  const [saveError, setSaveError] = React.useState(false);

  React.useEffect(() => {
    const questionId = respostas[activeTabIndex]?.question?.id;
    if (questionId && onQuestionChange) {
      onQuestionChange(questionId);
    }
  }, [activeTabIndex, respostas, onQuestionChange]);

  const debounceTimerRef = React.useRef(null);
  const initialAnswerRef = React.useRef('');

  React.useEffect(() => {
    if (!interview?.id) return;

    const handle = async () => {
      try {
        setLoading(true);
        const response = await prepareAnswers(interview.id);

        if (Array.isArray(response) && response.length > 0) {
          const sortedRespostas = [...response].sort(
            (a, b) => a.question.id - b.question.id,
          );

          setRespostas(sortedRespostas);

          const firstUnansweredIndex = sortedRespostas.findIndex(
            (item) =>
              !item.answer ||
              item.answer.trim().split(/\s+/).filter(Boolean).length < 120,
          );

          const tabToShow =
            firstUnansweredIndex === -1
              ? sortedRespostas.length - 1
              : firstUnansweredIndex;

          setActiveTabIndex(tabToShow);
          setCurrentAnswer(sortedRespostas[tabToShow]?.answer || '');
        } else {
          console.error('Dados inválidos recebidos:', response);
        }
      } catch (error) {
        console.error('Erro ao carregar perguntas:', error);
      } finally {
        setLoading(false);
      }
    };

    handle();
  }, [interview, setRespostas, prepareAnswers]);

  React.useEffect(() => {
    if (respostas.length > 0 && respostas[activeTabIndex]) {
      setCurrentAnswer(respostas[activeTabIndex]?.answer || '');
    }
  }, [activeTabIndex, respostas]);

  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    setShowValidation(false);
    setHasSaved(false);
    //setSaveError(false);
  }, [activeTabIndex]);

  const saveAnswer = React.useCallback(
    async (answerData) => {
      try {
        setSaving(true);
        setSaveError(false);
        await updateAnswer(answerData);
        setHasSaved(true);
      } catch (error) {
        console.error('Erro ao salvar resposta:', error);
        setSaveError(true);
      } finally {
        setSaving(false);
      }
    },
    [updateAnswer],
  );

  const handleAnswerChange = React.useCallback(
    (value) => {
      setCurrentAnswer(value);

      setRespostas((prev) => {
        const updated = [...prev];
        updated[activeTabIndex] = {
          ...updated[activeTabIndex],
          answer: value,
        };
        return updated;
      });

      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        const currentAnswerData = respostas[activeTabIndex];
        if (currentAnswerData) {
          const payload = {
            id: currentAnswerData.id,
            answer: value,
            answerFilled:
              value.trim().split(/\s+/).filter(Boolean).length >= 120,
            question: currentAnswerData.question,
          };

          saveAnswer(payload);
        }
      }, 1000);
    },
    [activeTabIndex, respostas, setRespostas, saveAnswer],
  );

  const wordCount = currentAnswer.trim().split(/\s+/).filter(Boolean).length;
  const canAdvance = wordCount >= 120 && !saveError;

  const textError = React.useMemo(() => {
    if (saveError) {
      return 'Erro ao salvar a resposta. Tente novamente antes de avançar.';
    }

    if (wordCount < 120) {
      return 'Você precisa preencher no mínimo 120 palavras para continuar.';
    }

    if (wordCount > 600) {
      return 'Atenção. Você ultrapassou o número máximo de 600 palavras.';
    }

    if (wordCount === 600) {
      return 'Você atingiu o número máximo de 600 palavras.';
    }

    if (wordCount >= 550) {
      return 'Atenção. Você está quase atingindo o número máximo de 600 palavras permitidas para a resposta.';
    }

    return '';
  }, [wordCount, saveError]);

  const inputStatus = React.useMemo(() => {
    const isUnderMinimum = showValidation && wordCount < 120;
    const isOverMaximum = wordCount > 600;
    const isAtMaximum = wordCount === 600;
    const isNearMaximum = wordCount >= 550 && wordCount < 600;

    return isUnderMinimum || isOverMaximum || isAtMaximum || isNearMaximum || saveError
      ? 'invalid'
      : 'normal';
  }, [showValidation, wordCount, saveError]);

  const handleNext = async () => {
    if (!canAdvance) {
      setShowValidation(true);
      return;
    }

    setShowValidation(false);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (activeTabIndex < respostas.length - 1) {
      setActiveTabIndex(activeTabIndex + 1);
      setHasSaved(false);
    }
  };

  const handleSaveInterview = React.useCallback(async () => {
    if (!interview || saveError) return;

    try {
      const payload = {
        idInterview: interview.id,
        answers: respostas,
      };
      await sendAnswerInterview(payload, interview.module.id);
      setFinishedInterview(true);
      setStatusInterview('PROCESSING');
    } catch (error) {
      setFinishedInterview(true);
      setStatusInterview('ERROR');
    }
  }, [
    respostas,
    setStatusInterview,
    setFinishedInterview,
    sendAnswerInterview,
    interview,
    saveError,
  ]);

  const handleBack = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (activeTabIndex > 0) {
      setActiveTabIndex(activeTabIndex - 1);
      setHasSaved(false);
      setShowValidation(false);
    }
  };

  const currentQuestion = respostas[activeTabIndex]?.question;
  const currentQuestionText = currentQuestion
    ? questionMap[currentQuestion.id]
    : '';

  const openDialogMessage = React.useCallback(() => {
    return showModal({
      actions: [],
      size: 'small',
      title: 'Roteiro de ajuda',
      dismissible: true,
      dismissibleIconColor: BaSeTheme.colors.defaultColors.black,
      dismissibleIconType: 'tertiary',
      content: ({ closeModal, emitEvent }) => (
        <div>
          <Lista question={currentQuestion?.id} />
        </div>
      ),
      onChooseAction: (action) => { },
    });
  }, [showModal, currentQuestion]);

  const stopRecordCOnfig = React.useCallback(() => {
    return showModal({
      actions: [
        {
          action: 'Ok',
          type: 'primary',
          color: BaSeTheme.colors.institucionais.azulSebrae10,
        },
      ],
      size: 'small',
      title: 'Gravação Finalizada',
      dismissible: true,
      dismissibleIconColor: BaSeTheme.colors.defaultColors.black,
      dismissibleIconType: 'tertiary',
      content: ({ closeModal, emitEvent }) => (
        <div>
          <BaSeParagraph>
            Estamos finalizando o recurso de voz.
            <br />
            <br />
            Revise seu texto e veja se está com as devidas pontuações ao final
            de cada frase e parágrafo.
            <br />
            <br />
            Caso o texto não esteja devidamente escrito e pontuado, a avaliação
            poderá não ser bem sucedida.
            <br />
            <br />
          </BaSeParagraph>
        </div>
      ),
    });
  }, [showModal]);

  const startRecordCOnfig = React.useCallback(() => {
    return showModal({
      actions: [
        {
          action: 'Ok',
          type: 'primary',
          color: BaSeTheme.colors.institucionais.azulSebrae10,
        },
      ],
      size: 'small',
      title: 'Iniciar Gravação',
      dismissible: true,
      dismissibleIconColor: BaSeTheme.colors.defaultColors.black,
      dismissibleIconType: 'tertiary',
      content: ({ closeModal, emitEvent }) => (
        <div>
          <BaSeParagraph>
            Estamos iniciando o recurso de voz, que será convertido em texto.
            <br />
            <br />
            Tenha atenção com a conversão, pois ela não é 100%.
            <br />
            <br />
            Ao finalizar, revise seu texto e veja se está com as devidas
            pontuações ao final de cada frase e parágrafo.
            <br />
            <br />
          </BaSeParagraph>
        </div>
      ),
      onChooseAction: (action) => {
        if (action === 'Ok') {
          initialAnswerRef.current = currentAnswer;

          resetTranscript();
          SpeechRecognition.startListening({
            continuous: true,
            language: 'pt-BR',
          });
        }
      },
    });
  }, [showModal, resetTranscript, currentAnswer]);

  const actionListeningMap = React.useMemo(
    () => ({
      true: () => {
        SpeechRecognition.stopListening();
        stopRecordCOnfig();
      },
      false: () => {
        startRecordCOnfig();
      },
    }),
    [startRecordCOnfig, stopRecordCOnfig],
  );

  React.useEffect(() => {
    if (!listening || !transcript) return;

    const wordCount = transcript.trim().split(/\s+/).filter(Boolean).length;
    if (wordCount >= 600) {
      actionListeningMap[true]();
      return;
    }

    const newAnswer = initialAnswerRef.current
      ? `${initialAnswerRef.current} ${transcript}`
      : transcript;

    handleAnswerChange(newAnswer);
  }, [transcript, actionListeningMap, handleAnswerChange, listening]);

  React.useEffect(() => {
    if (!listening) {
      initialAnswerRef.current = '';
    }
  }, [listening]);

  React.useEffect(() => {
    setShowValidation(false);
  }, [activeTabIndex]);

  const isTabDisabled = React.useCallback(
    (targetIndex) => {
      if (targetIndex < activeTabIndex) {
        return false;
      }

      if (targetIndex === activeTabIndex) {
        return false;
      }

      for (let i = 0; i < targetIndex; i++) {
        const stepData = respostas[i];
        const isComplete =
          !!stepData?.answer &&
          stepData.answer.trim().split(/\s+/).filter(Boolean).length >= 120;

        if (!isComplete) {
          return true;
        }
      }

      return false;
    },
    [activeTabIndex, respostas],
  );

  const handleTabChange = (newTabIndex) => {
    if (newTabIndex < activeTabIndex) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      setActiveTabIndex(newTabIndex);
      setHasSaved(false);
      setShowValidation(false);
      return;
    }

    if (newTabIndex > activeTabIndex) {
      if (!canAdvance) {
        setShowValidation(true);
        return;
      }

      setShowValidation(false);
      let canNavigate = true;

      for (let i = activeTabIndex; i < newTabIndex; i++) {
        const stepData = respostas[i];
        const isComplete =
          !!stepData?.answer &&
          stepData.answer.trim().split(/\s+/).filter(Boolean).length >= 120;

        if (!isComplete) {
          canNavigate = false;
          break;
        }
      }

      if (canNavigate) {
        if (debounceTimerRef.current) {
          clearTimeout(debounceTimerRef.current);
        }

        setActiveTabIndex(newTabIndex);
        setHasSaved(false);
        setShowValidation(false);
      } else {
        console.log('Complete as perguntas anteriores antes de avançar');
      }
    }
  };

  const smallScreen = window.innerWidth < 1024;

  const tabs = React.useMemo(() => {
    return respostas.map((item, index) => ({
      content: () => null,
      isDisabled: isTabDisabled(index),
      title: smallScreen
        ? {
          label: `${index + 1}`,
        }
        : {
          label: `Pergunta ${index + 1}`,
        },
    }));
  }, [respostas, isTabDisabled, smallScreen]);

  const toltipData = {
    direction: 'left',
    width: 230,
    message: [
      <InfoHeader key="header">
        <BaSeIcon
          name="lightbulb-alt"
          size="16"
          description="icon-lightulb-als"
          color={BaSeTheme.colors.institucionais.cinzaSebrae30}
        />
        <BaSeHeading6
          isBold
          color={BaSeTheme.colors.institucionais.cinzaSebrae30}
        >
          {listening
            ? 'Clique para parar a gravação'
            : 'Grave que convertemos em texto'}
        </BaSeHeading6>
        <BaSeIcon
          name="times"
          size="16"
          description="icon-times"
          color={BaSeTheme.colors.institucionais.cinzaSebrae30}
        />
      </InfoHeader>,
      <InfoBody key="body">
        <BaSeParagraph color={BaSeTheme.colors.institucionais.cinzaSebrae30}>
          {listening
            ? 'Gravação em andamento. Fale naturalmente e clique no botão vermelho para finalizar.'
            : 'Para te ajudar, você tem a opção de responder por áudio, utilizando o recurso nativo do seu teclado e tudo será convertido em texto'}
        </BaSeParagraph>

        {!listening && (
          <>
            <BaSeHeading6
              isBold
              color={BaSeTheme.colors.institucionais.cinzaSebrae30}
            >
              Fique atento
            </BaSeHeading6>
            <BaSeParagraph
              color={BaSeTheme.colors.institucionais.cinzaSebrae30}
            >
              Nem sempre a conversão fica 100%, então faça uma revisão e altere
              o texto que precisar ;)
            </BaSeParagraph>
          </>
        )}
      </InfoBody>,
    ],
  };

  const greenFilter =
    'invert(27%) sepia(92%) saturate(1016%) hue-rotate(119deg) brightness(97%) contrast(101%)';
  const blueFilter =
    'invert(11%) sepia(94%) saturate(5959%) hue-rotate(245deg) brightness(70%) contrast(140%)';

  const completedIndexes = respostas
    .map((item, index) => ({
      index,
      isCompleted:
        !!item.answer &&
        item.answer.trim().split(/\s+/).filter(Boolean).length >= 120,
    }))
    .filter((item) => item.isCompleted && item.index !== activeTabIndex)
    .map((item) => item.index);

  if (loading) {
    return (
      <div style={{ padding: '2em', textAlign: 'center' }}>
        Carregando perguntas...
      </div>
    );
  }

  if (respostas.length === 0) {
    return (
      <div style={{ padding: '2em', textAlign: 'center' }}>
        Nenhuma pergunta encontrada.
      </div>
    );
  }

  const buttonStyle = activeTabIndex + 1 === 1 ? { marginLeft: 'auto' } : {};
  return (
    <Container>
      <p>
        <strong>
          Você verá 5 situações. Para cada uma, conte um fato real em que você
          foi o protagonista e de preferência da sua vida empresarial.{' '}
        </strong>
        <br />
        Cada relato deve ter uma estrutura de começo, meio e fim, e você deve
        escolher histórias que se lembre bem e se sinta à vontade para
        compartilhar.
      </p>

      <Content>
        <Wrapper>
          <TabsWrapper
            activeIndex={activeTabIndex}
            activeColor={canAdvance ? '#008437' : '#0000C7'}
            activeFilter={canAdvance ? greenFilter : blueFilter}
            completedIndexes={completedIndexes}
          >
            <BaSeTabs
              activatedTabIndex={activeTabIndex}
              activeColor={canAdvance ? '#008437' : '#0000C7'}
              inactiveColor="#617385"
              onActivatedTabIndex={handleTabChange}
              tabGap={12}
              tabs={tabs}
            />
          </TabsWrapper>

          <ConteinerTextArea>
            {currentQuestionText && (
              <ContentPergunta>
                <div>
                  <Title>{currentQuestionText}</Title>
                  <SubTitle>
                    (Você pode digitar ou gravar um áudio clicando no microfone
                    ao lado)
                  </SubTitle>
                </div>
                {!breakpointController.inMobile ||
                  (!breakpointController.inTablet && (
                    <BaSeShapeButton
                      buttonType="button"
                      nameIcon="question-circle"
                      onClick={() => openDialogMessage()}
                      shape="square"
                      size="medium"
                      sizeIcon="medium"
                      type="tertiary"
                    />
                  ))}
              </ContentPergunta>
            )}

            <ContentTextarea>
              {browserSupportsSpeechRecognition && (
                <ContentMic>
                  <BaSeShapeButton
                    buttonType="button"
                    color={listening ? 'destructive' : 'default'}
                    nameIcon={
                      listening ? 'media/record-circle-line' : 'media/mic-fill'
                    }
                    shape="square"
                    size="small"
                    onClick={actionListeningMap[listening]}
                    sizeIcon="small"
                    tooltip={toltipData}
                    type="tertiary"
                  />
                </ContentMic>
              )}

              <ContentSaving>
                {listening ? (
                  <BaSeTag
                    color={BaSeTheme.colors.institucionais.cinzaSebrae45}
                    icon="record-audio-solid"
                    label="Gravando..."
                    limitedWidth={200}
                    overrideColor={{
                      iconColor: BaSeTheme.colors.institucionais.cinzaSebrae45,
                    }}
                    size="small"
                    themeMode="dark"
                    type="primary"
                  />
                ) : saving ? (
                  <BaSeTag
                    color={BaSeTheme.colors.institucionais.cinzaSebrae45}
                    icon="refresh-alt"
                    label="Salvando..."
                    limitedWidth={200}
                    overrideColor={{
                      iconColor: BaSeTheme.colors.institucionais.cinzaSebrae45,
                    }}
                    size="small"
                    themeMode="dark"
                    type="primary"
                  />
                ) : saveError ? (
                  <BaSeTag
                    color={BaSeTheme.colors.feedbackInterface.erro70}
                    icon="exclamation-triangle"
                    label="Erro ao salvar!"
                    limitedWidth={200}
                    overrideColor={{
                      iconColor: BaSeTheme.colors.feedbackInterface.erro70,
                    }}
                    size="small"
                    themeMode="dark"
                    type="primary"
                  />
                ) : hasSaved ? (
                  <BaSeTag
                    color={BaSeTheme.colors.institucionais.cinzaSebrae45}
                    icon="cloud-upload"
                    label="Resposta salva!"
                    limitedWidth={200}
                    overrideColor={{
                      iconColor: BaSeTheme.colors.institucionais.cinzaSebrae45,
                    }}
                    size="small"
                    themeMode="dark"
                    type="primary"
                  />
                ) : null}
              </ContentSaving>

              <BaSeTextArea
                hasCounter="words"
                placeholder='Lembre-se de iniciar o texto de sua resposta utilizando o pronome "Eu".'
                onChange={handleAnswerChange}
                rows={12}
                textSize="medium"
                value={currentAnswer}
                counterMinLength={120}
                counterMaxLength={600}
                textError={textError}
                inputStatus={inputStatus}
              />
            </ContentTextarea>
          </ConteinerTextArea>
        </Wrapper>

        <ConteinerButtons
          style={{ width: activeTabIndex + 1 === 1 ? '100%' : '' }}
        >
          {activeTabIndex + 1 !== 1 && (
            <CommonButton
              variant="secondary"
              foregroundColor="#0015C1"
              isBold={true}
              backgroundColor="#0000C7"
              disabled={activeTabIndex === 0}
              onClick={handleBack}
              iconLeft={
                <BaSeIcon
                  name="arrow-left"
                  size="32"
                  description="icon-lightulb-als"
                  color={'#0000C7'}
                />
              }
            >
              Voltar
            </CommonButton>
          )}

          <CommonButton
            variant="primary"
            isBold={true}
            style={buttonStyle}
            backgroundColor="#0000C7"
            disabled={!canAdvance}
            onClick={activeTabIndex === 4 ? handleSaveInterview : handleNext}
            iconRight={
              <BaSeIcon
                name="arrow-right"
                size="32"
                description="icon-lightulb-als"
                color={BaSeTheme.colors.defaultColors.white}
              />
            }
          >
            {activeTabIndex === 4 ? 'Enviar entrevista' : 'Próximo'}
          </CommonButton>
        </ConteinerButtons>
      </Content>
    </Container>
  );
};

export default QuestionnaireInterviewCommon;
